import { createContext, useContext, useState, useEffect, useCallback} from "react";
import { supabase } from "../supabase/supabaseClient";
import {
    getNotifications,
    getUnreadCount,
    subscribeToNotifications,
    unsubscribeFromNotifications,
} from "../services/notificationsService";

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
    const [user, setUser] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);

    // Load initial notifications
    const loadNotifications = useCallback(async (userId) => {
        try {
            const { data, error } = await getNotifications(userId, 20);
            if (!error && data) setNotifications(data);

            const { count, error: countError } = await getUnreadCount(userId);
            if (!countError) setUnreadCount(count || 0);
        } catch (err) {
            console.error("Error loading notifications:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Handle new notification from realtime
    const handleNewNotification = useCallback((notification) => {
        setNotifications((prev) => [notification, ...prev].slice(0, 20));
        if (!notification?.read_at) {
            setUnreadCount((prev) => prev + 1);
        }

        // Optional: Show browser notification if permitted
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
                new Notification(notification.title, {
                    body: notification.body,
                    icon: "/logo192.png",
                });
            } catch (err) {
                // Browser notifications may fail silently
            }
        }
    }, []);

    // Refresh unread count (call after marking as read)
    const refreshUnreadCount = useCallback(async () => {
        if (!user) return;
        try {
            const { count } = await getUnreadCount(user.id);
            setUnreadCount(count || 0);
        } catch (err) {
            console.error("Error refreshing unread count:", err);
        }
    }, [user]);

    // Refresh full notifications list
    const refreshNotifications = useCallback(async () => {
        if (!user) return;
        try {
            await loadNotifications(user.id);
        } catch (err) {
            console.error("Error refreshing notifications:", err);
        }
    }, [user, loadNotifications]);

    useEffect(() => {
        const channelRef = { current: null };
        let isMounted = true;

        const init = async () => {
            try {
                // Prefer getSession() (local) over getUser() (network) so auth state
                // doesn't appear signed-out during transient connectivity.
                const { data } = await supabase.auth.getSession();
                const authUser = data?.session?.user ?? null;

                if (!isMounted) return;

                if (authUser) {
                    console.debug(`NotificationProvider: initializing for user ${authUser.id}`);
                    setUser(authUser);
                    // Don't block app initialization on notifications I/O.
                    loadNotifications(authUser.id);

                    // Subscribe to realtime (idempotent in service)
                    channelRef.current = subscribeToNotifications(authUser.id, handleNewNotification);
                    console.debug("NotificationProvider: subscribed channel:", channelRef.current);

                    // Request browser notification permission
                    if (typeof Notification !== "undefined" && Notification.permission === "default") {
                        Notification.requestPermission().catch(() => {});
                    }
                } else {
                    setLoading(false);
                }
            } catch (err) {
                console.error("Error initializing notifications:", err);
                if (isMounted) setLoading(false);
            }
        };

        init();

        // Listen for auth changes
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (!isMounted) return;
            
            try {
                if (event === "SIGNED_IN" && session?.user) {
                    console.debug(`NotificationProvider: auth SIGNED_IN ${session.user.id}`);
                    setUser(session.user);
                    loadNotifications(session.user.id);
                    if (channelRef.current) {
                        unsubscribeFromNotifications(channelRef.current);
                        channelRef.current = null;
                    }
                    channelRef.current = subscribeToNotifications(session.user.id, handleNewNotification);
                    console.debug("NotificationProvider: subscribed new channel on SIGNED_IN", channelRef.current);
                } else if (event === "SIGNED_OUT") {
                    console.debug("NotificationProvider: auth SIGNED_OUT");
                    setUser(null);
                    setNotifications([]);
                    setUnreadCount(0);
                    if (channelRef.current) {
                        unsubscribeFromNotifications(channelRef.current);
                        channelRef.current = null;
                    }
                }
            } catch (err) {
                console.error("Error handling auth change:", err);
            }
        });

        return () => {
            isMounted = false;
            subscription?.unsubscribe();
            if (channelRef.current) {
                console.debug("NotificationProvider: cleanup - unsubscribing channel");
                unsubscribeFromNotifications(channelRef.current);
                channelRef.current = null;
            }
        };
    }, [loadNotifications, handleNewNotification]);

    const value = {
        user,
        notifications,
        unreadCount,
        loading,
        refreshUnreadCount,
        refreshNotifications,
        setNotifications,
        setUnreadCount,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotifications() {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error("useNotifications must be used within NotificationProvider");
    }
    return context;
}
